import React, { useEffect, useState } from 'react';
import { Form, Input, Select, Button, InputNumber, Tooltip, Tag } from 'antd';
import { useCampaign } from '../../context/CampaignContext';
import { WhatsAppOutlined, FacebookOutlined, InstagramOutlined } from '@ant-design/icons';
import coreAPIService from '../../apiServices/coreAPIService';
import { CTW_API_ENDPOINT } from '../../utils';

const { Option } = Select;

interface InstagramAccount {
    id: string;
    name: string;
    username?: string;
}

interface CampaignDetailsPanelProps {
    adAccounts: any[];
    adAccountLoading: boolean;
    handleAdAccountChange: (value: string) => void;
    orgs: any[];
    orgLoading: boolean;
    handleOrgChange: (value: string) => void;
    fbPages: any[];
    pagesLoading: boolean;
    handleFbPageChange: (value: string) => void;
    wabaList: any[];
    handleWabaChange: (value: string) => void;
    isMilestoneCheckPassed: boolean | null;
    checkingMilestones: boolean;
    isMilestoneCheckErrorMsg?: string | null;
    isAdmin: boolean;
}

export const CampaignDetailsPanel: React.FC<CampaignDetailsPanelProps> = ({
    adAccounts,
    adAccountLoading,
    handleAdAccountChange,
    orgs,
    orgLoading,
    handleOrgChange,
    fbPages,
    pagesLoading,
    handleFbPageChange,
    wabaList,
    handleWabaChange,
    isMilestoneCheckPassed,
    checkingMilestones,
    isMilestoneCheckErrorMsg,
    isAdmin,
}) => {
    const { state, dispatch } = useCampaign();

    const [instaAccounts, setInstaAccounts] = useState<InstagramAccount[]>([]);
    const [instaLoading, setInstaLoading] = useState(false);

    // Fetch Instagram accounts whenever the ad account changes
    useEffect(() => {
        if (!state.selectedAdAccountId) {
            setInstaAccounts([]);
            return;
        }

        const fetchInstaAccounts = async () => {
            setInstaLoading(true);
            try {
                const res = await coreAPIService.get<any>(
                    CTW_API_ENDPOINT,
                    `v2/meta-integration/instagram-accounts?adAccountId=${state.selectedAdAccountId}`
                );
                if (res.meta?.code === 200 && Array.isArray(res.data)) {
                    setInstaAccounts(res.data);
                } else {
                    setInstaAccounts([]);
                }
            } catch {
                setInstaAccounts([]);
            } finally {
                setInstaLoading(false);
            }
        };

        fetchInstaAccounts();
    }, [state.selectedAdAccountId]);

    const handleChatWithBot = (number: any) => {
        window.open(`https://wa.me/${number}?text=Hello`, '_blank');
    };

    const togglePlatform = (platform: string) => {
        const current = state.selectedPlatforms;
        let updated: string[];
        if (current.includes(platform)) {
            // Prevent deselecting both
            if (current.length === 1) return;
            updated = current.filter((p) => p !== platform);
        } else {
            updated = [...current, platform];
        }
        dispatch({ type: 'SET_FIELD', field: 'selectedPlatforms', value: updated });

        // If instagram is deselected, also clear the instagram account id
        if (platform === 'instagram' && !updated.includes('instagram')) {
            dispatch({ type: 'SET_FIELD', field: 'instagramAccountId', value: null });
        }
    };

    const isFbActive = state.selectedPlatforms.includes('facebook');
    const isInstaActive = state.selectedPlatforms.includes('instagram');

    return (
        <Form layout="vertical" className="mt-4 space-y-4">
            {/* Campaign Name & Objective */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Form.Item label="Account" required>
                    <Select
                        loading={orgLoading}
                        value={state.orgId}
                        onChange={handleOrgChange}
                        placeholder="Select organization"
                        showSearch
                        optionFilterProp="children"
                        disabled={state.isEditing}
                    >
                        {orgs.map((org) => (
                            <Option key={org.orgId} value={org.orgId}>
                                {org.orgName}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>

                <Form.Item label="Meta Ad Account" required>
                    <Select
                        loading={adAccountLoading}
                        value={state.selectedAdAccountId}
                        onChange={handleAdAccountChange}
                        disabled={!isAdmin || state.isEditing}
                        placeholder="Select ad account"
                        showSearch
                        optionFilterProp="children"
                    >
                        {adAccounts.map((acc) => (
                            <Option key={acc.id} value={acc.id}>
                                {acc.name}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>
            </div>

            {/* Ad Account & Org Selection */}
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Account Setup</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Form.Item label="Campaign Name" required>
                        <Input
                            value={state.campaignName}
                            onChange={(e) => dispatch({ type: 'SET_FIELD', field: 'campaignName', value: e.target.value })}
                            placeholder="Enter campaign name"
                            className="rounded-md"
                            disabled={state.isEditing}
                        />
                    </Form.Item>

                    <Form.Item label="Objective">
                        <Select
                            value={state.selectedObjective}
                            onChange={(value) => dispatch({ type: 'SET_FIELD', field: 'selectedObjective', value })}
                            className="rounded-md"
                            disabled={state.isEditing}
                        >
                            <Option value="OUTCOME_ENGAGEMENT">Engagement</Option>
                            <Option value="OUTCOME_SALES">Sales</Option>
                            <Option value="OUTCOME_LEADS">Leads</Option>
                        </Select>
                    </Form.Item>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <Form.Item label="Facebook Page" required>
                        <Select
                            key={`fb-page-select-${fbPages.length}`}
                            loading={pagesLoading}
                            value={state.selectedFbPageId}
                            onChange={handleFbPageChange}
                            placeholder="Select Facebook Page"
                            showSearch
                            optionFilterProp="children"
                            disabled={state.isEditing}
                        >
                            {fbPages.map((page) => (
                                <Option key={page.id} value={page.id}>
                                    {page.business + " | " + page.name}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        label="WhatsApp Business Account (WABA)"
                        required
                        help={
                            checkingMilestones ? "Checking milestones..." :
                                isMilestoneCheckPassed === false ? (
                                    <span className="text-red-500">
                                        {isMilestoneCheckErrorMsg || "Milestone check failed"}
                                    </span>
                                ) : null
                        }
                    >
                        <div className="flex items-center gap-2">
                            <Select
                                value={state.selectedWaba}
                                onChange={handleWabaChange}
                                placeholder="Select WABA"
                                showSearch
                                optionFilterProp="children"
                                status={isMilestoneCheckPassed === false ? "error" : ""}
                                virtual={false}
                                disabled={state.isEditing}
                                className="flex-1"
                            >
                                {wabaList.map((waba) => (
                                    <Option key={waba.wabaNumber} value={waba.wabaNumber}>
                                        {`${waba.wabaNumber} (${waba.subdomain})`}
                                    </Option>
                                ))}
                            </Select>

                            <Tooltip title="Test bot journey">
                                <Button
                                    type="text"
                                    size="middle"
                                    icon={<WhatsAppOutlined />}
                                    onClick={() => handleChatWithBot(state.selectedWaba)}
                                    disabled={!state.selectedWaba}
                                    className={`border border-gray-300 rounded-md ${!state.selectedWaba ? 'bg-gray-200' : 'bg-green-200'
                                        }`}
                                />
                            </Tooltip>
                        </div>
                    </Form.Item>
                </div>
            </div>

            {/* Platform Selection */}
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Platform &amp; Placement</h3>

                <Form.Item label="Publish on">
                    <div className="flex items-center gap-3">
                        {/* Facebook toggle */}
                        <Tooltip title="Facebook cannot be deselected">
                            <button
                                type="button"
                                disabled
                                onClick={() => togglePlatform('facebook')}
                                className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-all duration-200 font-medium text-sm
                                ${isFbActive
                                        ? 'border-blue-600 bg-blue-50 text-blue-700 shadow-sm'
                                        : 'border-gray-300 bg-white text-gray-500 hover:border-blue-300'
                                    }`}
                            >
                                <FacebookOutlined className={isFbActive ? 'text-blue-600' : 'text-gray-400'} />
                                Facebook
                                {isFbActive && (
                                    <span className="ml-1 w-2 h-2 rounded-full bg-blue-600 inline-block" />
                                )}
                            </button>
                        </Tooltip>


                        {/* Instagram toggle */}
                        <button
                            type="button"
                            onClick={() => togglePlatform('instagram')}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-all duration-200 font-medium text-sm
                                ${isInstaActive
                                    ? 'border-pink-500 bg-pink-50 text-pink-700 shadow-sm'
                                    : 'border-gray-300 bg-white text-gray-500 hover:border-pink-300'
                                }`}
                        >
                            <InstagramOutlined className={isInstaActive ? 'text-pink-500' : 'text-gray-400'} />
                            Instagram
                            {isInstaActive && (
                                <span className="ml-1 w-2 h-2 rounded-full bg-pink-500 inline-block" />
                            )}
                        </button>
                    </div>
                </Form.Item>

                {/* Instagram Account dropdown — shown only when Instagram is selected */}
                {isInstaActive && (
                    <Form.Item
                        label="Instagram Account (optional)"
                        className="mt-2"
                    >
                        <Select
                            loading={instaLoading}
                            value={state.instagramAccountId}
                            onChange={(val) =>
                                dispatch({ type: 'SET_FIELD', field: 'instagramAccountId', value: val ?? null })
                            }
                            placeholder={
                                !state.selectedAdAccountId
                                    ? 'Select a Meta Ad Account first'
                                    : instaLoading
                                        ? 'Loading Instagram accounts...'
                                        : 'Select Instagram account (optional)'
                            }
                            disabled={!state.selectedAdAccountId}
                            allowClear
                            showSearch
                            optionFilterProp="children"
                            style={{ maxWidth: 400 }}
                        >
                            {instaAccounts.map((acc) => (
                                <Option key={acc.id} value={acc.id}>
                                    {acc.name || acc.username || acc.id}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                )}
            </div>
        </Form>
    );
};
